import React from 'react';

const SIGNAL_COLORS = {
  BULLISH: '#00ff88',
  NEUTRAL: '#f59e0b',
  BEARISH: '#ef4444'
};

const SIGNAL_BG = {
  BULLISH: 'rgba(0,255,136,0.12)',
  NEUTRAL: 'rgba(245,158,11,0.12)',
  BEARISH: 'rgba(239,68,68,0.12)'
};

function ScoreMeter({ score, signal }) {
  const color = SIGNAL_COLORS[signal] || '#64748b';
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const dash = (score / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
      <svg width="96" height="96" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="48" cy="48" r={radius} fill="none" stroke="#1e2d3d" strokeWidth="8" />
        <circle cx="48" cy="48" r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>/ 100</span>
      </div>
    </div>
  );
}

export default function ShareCard({ card, showButtons = true, cardRef }) {
  const { ticker, signal, score, price, companyName, createdAt } = card;
  const color = SIGNAL_COLORS[signal] || '#64748b';
  const signalBg = SIGNAL_BG[signal] || 'rgba(100,116,139,0.1)';
  const dateStr = createdAt ? new Date(createdAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '';

  const cardStyle = {
    background: 'linear-gradient(135deg, #0f1923 0%, #1a2535 100%)',
    border: `1px solid ${color}33`,
    borderRadius: 16,
    padding: '28px 28px 20px',
    maxWidth: 420,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    position: 'relative',
    overflow: 'hidden'
  };

  return (
    <div>
      {/* Capturable card */}
      <div ref={cardRef} style={cardStyle}>
        {/* Corner accent */}
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 120, height: 120,
          background: `radial-gradient(circle at top right, ${color}18, transparent 70%)`,
          pointerEvents: 'none'
        }} />

        {/* Header: logo + date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6,
              background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, color: '#0f1923'
            }}>M</div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em' }}>
              MarketPulze
            </span>
          </div>
          {dateStr && (
            <span style={{ fontSize: 11, color: '#475569' }}>{dateStr}</span>
          )}
        </div>

        {/* Company + ticker */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.2 }}>
            {companyName || ticker}
          </div>
          <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>
            {ticker}
            {price && (
              <span style={{ color: '#94a3b8', marginLeft: 12 }}>
                ${typeof price === 'number' ? price.toFixed(2) : price}
              </span>
            )}
          </div>
        </div>

        {/* Signal + score row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: signalBg, border: `1px solid ${color}44`,
              borderRadius: 8, padding: '8px 16px'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 18, fontWeight: 800, color, letterSpacing: '0.05em' }}>
                {signal}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 8, paddingLeft: 2 }}>
              Algorithmic signal
            </div>
          </div>
          <ScoreMeter score={score} signal={signal} />
        </div>

        {/* Disclaimer bar */}
        <div style={{
          borderTop: '1px solid #1e2d3d', paddingTop: 12, marginTop: 4,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span style={{ fontSize: 10, color: '#475569' }}>
            Algorithmic signal · Not investment advice
          </span>
          <span style={{ fontSize: 10, color: '#475569' }}>
            Data delayed 15 min
          </span>
        </div>

        {/* URL watermark */}
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <span style={{ fontSize: 10, color: '#334155', letterSpacing: '0.06em' }}>
            marketpulze.com
          </span>
        </div>
      </div>

      {/* Share action buttons — NOT captured in PNG */}
      {showButtons && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <DownloadButton cardRef={cardRef} ticker={ticker} signal={signal} />
          <CopyLinkButton />
          <TwitterButton ticker={ticker} signal={signal} score={score} />
        </div>
      )}
    </div>
  );
}

function DownloadButton({ cardRef, ticker, signal }) {
  const handleDownload = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `marketpulze-${ticker}-${signal.toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('PNG export failed:', e);
    }
  };

  return (
    <button onClick={handleDownload} style={{
      background: '#1a2535', border: '1px solid #2d4060', borderRadius: 8,
      color: '#94a3b8', padding: '8px 14px', fontSize: 13, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6
    }}>
      ↓ Save PNG
    </button>
  );
}

function CopyLinkButton() {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button onClick={handleCopy} style={{
      background: copied ? '#00ff8822' : '#1a2535',
      border: `1px solid ${copied ? '#00ff8866' : '#2d4060'}`,
      borderRadius: 8, color: copied ? '#00ff88' : '#94a3b8',
      padding: '8px 14px', fontSize: 13, cursor: 'pointer'
    }}>
      {copied ? '✓ Copied!' : '🔗 Copy Link'}
    </button>
  );
}

function TwitterButton({ ticker, signal, score }) {
  const text = encodeURIComponent(
    `$${ticker} is showing a ${signal} signal (score: ${score}/100) on MarketPulze — algorithmic analysis, not investment advice.`
  );
  const url = encodeURIComponent(window.location.href);
  const href = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      background: '#1a2535', border: '1px solid #2d4060', borderRadius: 8,
      color: '#94a3b8', padding: '8px 14px', fontSize: 13, cursor: 'pointer',
      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6
    }}>
      𝕏 Share
    </a>
  );
}
